import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import bookingApi from '@/features/public/booking/api/bookingApi.ts';
import { bookingApiQueryKeys } from '@/features/public/booking/api/bookingApi.queryKeys.ts';
import {
  ClientFormWithCustomFields,
  generateFormKey,
} from '@/features/public/booking/schemas/clientSchema.ts';
import { toBookingRequest } from '@/features/public/booking/types/BookingSpecification.mapper.ts';
import { BookingSpecificationRequest } from '@/features/public/booking/types/BookingSpecificationRequest.ts';
import CustomFieldDefinitionDto from '@/features/dashboard/tourCustomFields/types/_CustomFieldDefinitionDto.ts';
import { useRegistrationAvailabilityGuard } from '@/features/public/booking/hooks/useRegistrationAvailabilityGuard.ts';

/**
 * Pełna wycena (krok 2): kody promocyjne w kluczu — ich zmiana wyzwala
 * przeliczenie. Odpowiedź niesie paymentPlan tylko gdy wycena jest ostateczna.
 */
export const getBookingQuoteQueryOptions = (
  orgSlug: string,
  tourSlug: string,
  clientForm: ClientFormWithCustomFields,
  fieldDefinitions: CustomFieldDefinitionDto[],
  promoCodes: string[] = [],
) => {
  const clientFormKey = generateFormKey(clientForm);

  return queryOptions({
    queryKey: bookingApiQueryKeys.quote(
      orgSlug,
      tourSlug,
      clientFormKey,
      promoCodes,
    ),
    queryFn: () =>
      bookingApi.getQuote(
        orgSlug,
        tourSlug,
        toBookingRequest(clientForm, fieldDefinitions, promoCodes),
      ),
    gcTime: 0,
    staleTime: 0,
    // Zachowaj poprzednie podsumowanie podczas przeliczania po zmianie kodu rabatowego.
    placeholderData: keepPreviousData,
  });
};

/** Atrapa wchodzi tylko w puste pole — żadne z podstawianych nie wpływa na cenę. */
const orPlaceholder = (
  value: string | undefined,
  placeholder: string,
): string => (value?.trim() ? value : placeholder);

/** Rezerwujący nie jest wyceniany (wyceniają się uczestnicy), więc jego data może być dowolna. */
const PLACEHOLDER_BIRTH_DATE = '1990-01-01';

/**
 * Formularz w trakcie wypełniania, doprowadzony do kształtu, który backend w
 * ogóle przyjmie.
 *
 * `/quote` jest `@Valid` dokładnie tak samo jak `/book`: `@NotBlank` na imieniu
 * i nazwisku — również **uczestnika** — `@Email` na adresie, `@NotBlank` na
 * ulicy, mieście i kodzie, `@NotNull` na telefonie. Bez atrap wybranie daty
 * urodzenia przed wpisaniem imienia kończyło estymatę serią 400, a cena w
 * stopce po cichu przestawała się odświeżać.
 */
export const toEstimateRequest = (
  clientForm: ClientFormWithCustomFields,
  fieldDefinitions: CustomFieldDefinitionDto[],
): BookingSpecificationRequest => {
  const request = toBookingRequest(clientForm, fieldDefinitions);

  return {
    ...request,
    client: {
      ...request.client,
      firstName: orPlaceholder(request.client.firstName, 'Imię'),
      lastName: orPlaceholder(request.client.lastName, 'Nazwisko'),
      email: orPlaceholder(request.client.email, 'rezerwacja@example.com'),
      // Numer podstawiamy zawsze: wpisywany bywa niepełny, a backend trzyma
      // `@NotBlank` na obu częściach.
      phoneNumber: { dialCode: '+48', number: '000000000' },
      birthDate: clientForm.birthDate
        ? request.client.birthDate
        : PLACEHOLDER_BIRTH_DATE,
      street: orPlaceholder(request.client.street, 'Ulica 1'),
      city: orPlaceholder(request.client.city, 'Miasto'),
      postalCode: orPlaceholder(request.client.postalCode, '00-000'),
    },
    participants: request.participants.map((participant) => ({
      ...participant,
      firstName: orPlaceholder(participant.firstName, 'Imię'),
      lastName: orPlaceholder(participant.lastName, 'Nazwisko'),
    })),
  };
};

/**
 * Bez daty urodzenia backend nie ma po czym dobrać progu cenowego, a uczestnik
 * bez wieku wychodzi w wycenie jako noworodek i wraca z
 * `NO_BASE_PRICE_AVAILABLE_FOR_PARTICIPANT_WITH_AGE` — błędem o niczym, bo
 * klient dopiero wypełnia formularz. Do czasu podania wieku wszystkich
 * uczestników nie ma czego liczyć.
 */
export const canEstimateBookingQuote = (
  clientForm: ClientFormWithCustomFields,
): boolean => {
  const participants = clientForm.participants ?? [];
  return participants.length > 0 && participants.every((p) => !!p.birthDate);
};

export const bookingQuoteEstimateQueryOptions = (
  orgSlug: string,
  tourSlug: string,
  clientForm: ClientFormWithCustomFields,
  fieldDefinitions: CustomFieldDefinitionDto[],
  clientFormKey: string = generateFormKey(clientForm),
) =>
  queryOptions({
    queryKey: bookingApiQueryKeys.quote(orgSlug, tourSlug, clientFormKey, []),
    queryFn: () =>
      bookingApi.getQuote(
        orgSlug,
        tourSlug,
        toEstimateRequest(clientForm, fieldDefinitions),
      ),
    placeholderData: keepPreviousData,
  });

/**
 * Ciągła wycena na kroku 1 — formularz bywa niekompletny, a brakujące dane
 * osobowe zastępuje `toEstimateRequest`.
 *
 * `submittedFormKey` to klucz formularza, który właśnie poszedł do wysyłki.
 * Dopóki opisuje ten sam formularz, wygrywa z opóźnionym: kliknięcie w pół
 * sekundy po ostatniej zmianie blokowało wysyłkę wyceną, której klient jeszcze
 * nie widział — przewijanie trafiało wtedy w pustkę.
 */
export const useBookingQuoteEstimate = (
  orgSlug: string,
  tourSlug: string,
  clientForm: ClientFormWithCustomFields,
  fieldDefinitions: CustomFieldDefinitionDto[],
  submittedFormKey?: string,
) => {
  const clientFormKey = generateFormKey(clientForm);
  const [debouncedClientFormKey] = useDebounce(clientFormKey, 500);
  const activeFormKey =
    submittedFormKey === clientFormKey ? clientFormKey : debouncedClientFormKey;

  const query = useQuery({
    ...bookingQuoteEstimateQueryOptions(
      orgSlug,
      tourSlug,
      clientForm,
      fieldDefinitions,
      activeFormKey,
    ),
    enabled: canEstimateBookingQuote(clientForm),
  });

  // Zapisy mogły się zamknąć (albo miejsca skończyć) w trakcie wypełniania
  // formularza — wtedy przeładuj route zamiast zostawiać klienta przy cenie.
  useRegistrationAvailabilityGuard(query.error, orgSlug, tourSlug);

  return query;
};
